import { useState } from "react";
import { AlertCircle, CircleStop, Coins, Loader2, Save } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { CopyButton } from "@/components/chat/CopyButton";
import { ResearchReport } from "@/components/research/ResearchReport";
import { SaveToProjectDialog } from "@/components/research/SaveToProjectDialog";
import { SearchProgress } from "@/components/research/SearchProgress";
import { SourceList } from "@/components/research/SourceCard";
import { useResearchStream } from "@/hooks/useResearchStream";
import { citedSet, DEPTHS, plural } from "@/lib/research";


function Header({ research, running, sources }) {
  const meta = DEPTHS[research.depth] || DEPTHS.quick;
  const Icon = meta.icon;
  const searches = research.plan?.length || 0;
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-zinc-500">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] px-2 py-0.5 text-[12px] text-zinc-200" data-testid="chat-research-label">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        {meta.label}
      </span>
      {running ? (
        <span className="inline-flex items-center gap-1.5 text-zinc-400" data-testid="chat-research-working"><Loader2 className="h-3 w-3 animate-spin" /> Working on it</span>
      ) : (
        <span data-testid="chat-research-summary">{[searches && plural(searches, "search"), sources.length && plural(sources.length, "source")].filter(Boolean).join(" · ")}</span>
      )}
    </div>
  );
}

function Outcome({ status, error, research }) {
  const charged = research.credits_charged || 0;
  if (error)
    return (
      <div className="flex items-start gap-2 text-[12.5px] text-rose-300/90" data-testid="chat-research-error">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {error.message}
          {status === "failed" && charged === 0 && <span className="text-zinc-500"> No credits were charged.</span>}
          {error.request_id && <span className="ml-2 font-mono text-zinc-600">Ref {error.request_id}</span>}
        </span>
      </div>
    );
  if (status === "cancelled")
    return (
      <div className="flex items-center gap-2 text-[12.5px] text-zinc-500" data-testid="chat-research-cancelled">
        <CircleStop className="h-3.5 w-3.5" /> You stopped this search. {charged ? `${plural(charged, "credit")} used for the part that ran.` : "No credits were charged."}
      </div>
    );
  return null;
}

// A Fast or Deep Search answer inside a chat: live progress from the real pipeline, then the cited report with its sources.
export function ResearchMessage({ message, research }) {
  const running = message.status === "streaming";
  const live = useResearchStream(research.id, running);
  const [saving, setSaving] = useState(false);
  const snap = live || { stage: research.stage || "preparing", plan: [], sources: [], text: "" };
  const text = (running ? snap.text : research.report_md) || "";
  const sources = (running ? snap.sources : research.sources) || [];
  const status = running ? "running" : research.status;
  const error = running ? live?.error : status === "failed" ? { message: research.error_message, request_id: research.request_id } : null;
  const showReport = !running || Boolean(text) || snap.stage === "writing";
  return (
    <div className="flex gap-3.5 sm:gap-4 cs-rise" data-testid="chat-message-research" data-status={status} data-depth={research.depth}>
      <div className="pt-0.5"><LogoMark size={26} /></div>
      <div className="min-w-0 flex-1 space-y-4">
        <Header research={research} running={running} sources={sources} />
        {running && (
          <div className="rounded-2xl border border-white/[0.08] bg-ink-900 px-4 py-3.5" data-testid="chat-research-progress">
            <SearchProgress depth={research.depth} live={snap} />
          </div>
        )}
        {sources.length > 0 && <SourceList compact sources={sources} cited={citedSet(text)} initial={4} />}
        {showReport && <ResearchReport text={text} sources={sources} live={running} />}
        <Outcome status={status} error={error} research={research} />
        {!running && (
          <div className="-ml-2 flex flex-wrap items-center gap-0.5" data-testid="chat-research-actions">
            {message.content && <CopyButton text={message.content} testId="chat-research-copy" />}
            {status === "complete" && (
              <button type="button" onClick={() => setSaving(true)} className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12px] text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-100" data-testid="chat-research-save">
                <Save className="h-3.5 w-3.5" /> Save to project
              </button>
            )}
            {status !== "failed" && (
              <span className="inline-flex h-7 items-center gap-1.5 px-2 text-[12px] text-zinc-600" data-testid="chat-research-credits">
                <Coins className="h-3.5 w-3.5" /> {plural(research.credits_charged || 0, "credit")}
              </span>
            )}
          </div>
        )}
      </div>
      {saving && <SaveToProjectDialog session={research} open={saving} onOpenChange={setSaving} />}
    </div>
  );
}
